import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Avatar,
  Box,
  Tooltip,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  Typography,
} from "@mui/material";
import NewStatsPageModal from "bundles/Internal/components/charts/NewStatsPageModal";
import { useCandidateModal } from "bundles/Internal/components/common/CandidateModalProvider";
import ApplyToOfferDialog from "bundles/Internal/components/common/ApplyToOfferDialog";
import CreateProfileDrawer from "bundles/Internal/components/common/CreateProfileDrawer";
import CreatePipelineDrawer from "bundles/Internal/components/dashboard/CreatePipelineDrawer";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import sh from "bundles/common/utils/sh";
import { appendFormValue } from "bundles/Internal/utils/candidateFormData";
import {
  Users,
  Eye,
  UserPlus,
  ChevronDown,
  ChevronRight,
  PanelLeftClose,
  PanelLeft,
  LogOut,
  LayoutDashboard,
  Contact,
  AlignStartVertical,
  GitBranchPlus,
  Clock,
  Briefcase,
  BriefcaseBusiness,
  Building2,
  FileText,
  BarChart3,
  LineChart,
  PieChart,
  TrendingUp,
  Activity,
  Zap,
  Logs,
  Plus,
  Lightbulb,
  CreditCard,
  CalendarSearch,
  Trophy,
  Flag,
  Archive,
  Folder,
  FolderOpen,
  Table2,
  FilePlus,
  Star,
} from "lucide-react";

const EXTERNAL_PROFILE_CREATED_EVENT = "external-profile:created";
const JOB_APPLICATION_CREATED_EVENT = "job-application:created";

const STATS_PAGE_ICONS = [BarChart3, LineChart, PieChart, TrendingUp, Activity, Zap, Users, Briefcase, Building2, CreditCard];

const getStatsPageIcon = (pageId) => {
  return STATS_PAGE_ICONS[Number(pageId) % STATS_PAGE_ICONS.length];
};

const InternalAppBar = ({ open: openProp, onToggle: onToggleProp, drawerWidth: drawerWidthProp } = {}) => {
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const currentInternalUser = useSelector((state) => state.current_internal_user);
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { openCreateCandidate } = useCandidateModal();
  const [applyToJobOpen, setApplyToJobOpen] = useState(false);
  const [createProfileOpen, setCreateProfileOpen] = useState(false);
  const [createPipelineOpen, setCreatePipelineOpen] = useState(false);

  const open = Boolean(openProp);
  const onToggle = typeof onToggleProp === "function" ? onToggleProp : undefined;
  const drawerWidth = drawerWidthProp || (open ? 240 : 56);

  const [statsPages, setStatsPages] = useState([]);
  const [newPageModalOpen, setNewPageModalOpen] = useState(false);
  const [newPageSubmitting, setNewPageSubmitting] = useState(false);

  useEffect(() => {
    sh.get("/internal/stats_pages")
      .then((res) => setStatsPages(res.data))
      .catch(() => {});
  }, []);

  const handleCreatePage = ({ title }, { reset }) => {
    setNewPageSubmitting(true);
    sh.post("/internal/stats_pages", { stats_page: { title } })
      .then((res) => {
        setStatsPages((prev) => [...prev, res.data]);
        setNewPageModalOpen(false);
        reset();
        navigate(`/internal/stats/${res.data.id}`);
      })
      .catch(() => {})
      .finally(() => setNewPageSubmitting(false));
  };

  const isDashboardRoute = location?.pathname === "/internal/dashboard";
  const isCoordinatorDashRoute = location?.pathname?.startsWith("/internal/coordinator-dashboard") || location?.pathname?.startsWith("/internal/coordinator-pipelines");
  const isTalentRankingsRoute = location?.pathname?.startsWith("/internal/talent-rankings");
  const isPipelinesRoute = location?.pathname === "/internal/pipelines";
  const isCandidatesSection = location?.pathname?.startsWith("/internal/candidates") || location?.pathname === "/internal";
  const isToBeReviewedRoute = location?.pathname?.startsWith("/internal/candidates/to_be_reviewed");
  const isFrenchMarketRoute = location?.pathname?.startsWith("/internal/candidates/french_market");
  const isArchivedRoute = location?.pathname?.startsWith("/internal/candidates/archived");
  const isCvVaultRoute = location?.pathname?.startsWith("/internal/cv-vault");
  const isCvTableRoute = location?.pathname?.startsWith("/internal/cv-table");
  const isActiveCandidatesRoute = isCandidatesSection && !isToBeReviewedRoute && !isFrenchMarketRoute && !isArchivedRoute;
  const isExternalProfilesRoute = location?.pathname?.startsWith("/internal/external_profiles");
  const isJobApplicationsRoute = location?.pathname?.startsWith("/internal/job_applications");
  const isInvitationsRoute = location?.pathname?.startsWith("/internal/users/invitations");
  const isRecruitersRoute = location?.pathname?.startsWith("/internal/recruiters");
  const isCompaniesRoute = location?.pathname?.startsWith("/internal/companies");
  const isJobOffersRoute = location?.pathname?.startsWith("/internal/job_offers");
  const isBlogPostsRoute = location?.pathname?.startsWith("/internal/blog_posts");
  const isTimeTrackerRoute = location?.pathname?.startsWith("/internal/time-tracker");
  const isProjectTimeStatsRoute = location?.pathname?.startsWith("/internal/time-project");
  const isCandidateInterestsRoute = location?.pathname?.startsWith("/internal/candidate_interests");
  const isSuggestionsRoute = location?.pathname?.startsWith("/internal/suggestions");
  const isCreditsRoute = location?.pathname?.startsWith("/internal/credits");
  const isPlatformAnalyticsRoute = location?.pathname?.startsWith("/internal/platform-analytics");
  const isStatsRoute = location?.pathname?.startsWith("/internal/stats/");
  const activeStatsPageId = isStatsRoute ? location.pathname.split("/internal/stats/")[1] : null;
  const isAdmin = currentInternalUser?.role === "admin";
  const isCoordinator = currentInternalUser?.role === "coordinator";
  const isAdminOrCoordinator = isAdmin || isCoordinator;

  const navGroups = [
    {
      label: "Talent",
      items: [
        {
          title: "Dashboard",
          icon: LayoutDashboard,
          active: isDashboardRoute,
          onClick: () => navigate("/internal/dashboard"),
        },
        ...(isAdminOrCoordinator
          ? [
            {
              title: "Team Performance",
              icon: AlignStartVertical,
              active: isCoordinatorDashRoute,
              onClick: () => navigate("/internal/coordinator-dashboard"),
            },
          ]
          : []),
        {
          title: "Hall of Fame",
          icon: Trophy,
          active: isTalentRankingsRoute,
          onClick: () => navigate("/internal/talent-rankings"),
        },
        {
          title: "Pipelines",
          icon: GitBranchPlus,
          active: isPipelinesRoute,
          onClick: () => navigate("/internal/pipelines"),
        },
        {
          title: "External Profiles",
          icon: Contact,
          active: isExternalProfilesRoute,
          onClick: () => navigate("/internal/external_profiles"),
        },
        {
          title: "CV vault",
          icon: isCvVaultRoute ? FolderOpen : Folder,
          active: isCvVaultRoute,
          onClick: () => navigate("/internal/cv-vault"),
        },
        {
          title: "CV table",
          icon: Table2,
          active: isCvTableRoute,
          onClick: () => navigate("/internal/cv-table"),
        },
      ],
    },
    {
      label: "Candidates",
      items: [
        {
          title: "Active Candidates",
          icon: Users,
          active: isActiveCandidatesRoute,
          onClick: () => navigate("/internal/candidates"),
        },
        {
          title: "To be reviewed",
          icon: Eye,
          active: isToBeReviewedRoute,
          onClick: () => navigate("/internal/candidates/to_be_reviewed"),
        },
        {
          title: "French Market",
          icon: Flag,
          active: isFrenchMarketRoute,
          onClick: () => navigate("/internal/candidates/french_market"),
        },
        {
          title: "Archived",
          icon: Archive,
          active: isArchivedRoute,
          onClick: () => navigate("/internal/candidates/archived"),
        },
        {
          title: "Job Applications",
          icon: FileText,
          active: isJobApplicationsRoute,
          onClick: () => navigate("/internal/job_applications"),
        },
      ],
    },
    {
      label: "Clients",
      items: [
        {
          title: "Recruiters",
          icon: Briefcase,
          active: isRecruitersRoute,
          onClick: () => navigate("/internal/recruiters"),
        },
        {
          title: "Companies",
          icon: Building2,
          active: isCompaniesRoute,
          onClick: () => navigate("/internal/companies"),
        },
        {
          title: "Job Offers",
          icon: BriefcaseBusiness,
          active: isJobOffersRoute,
          onClick: () => navigate("/internal/job_offers"),
        },
        {
          title: "Credits",
          icon: CreditCard,
          active: isCreditsRoute,
          onClick: () => navigate("/internal/credits"),
        },
        {
          title: "Candidate Interests",
          icon: Star,
          active: isCandidateInterestsRoute,
          onClick: () => navigate("/internal/candidate_interests"),
        },
        {
          title: "Suggestions",
          icon: Lightbulb,
          active: isSuggestionsRoute,
          onClick: () => navigate("/internal/suggestions"),
        },
      ],
    },
    {
      label: "Content",
      items: [
        {
          title: "Blogs",
          icon: FileText,
          active: isBlogPostsRoute,
          onClick: () => navigate("/internal/blog_posts"),
        },
      ],
    },
    {
      label: "Time tracker",
      items: [
        {
          title: "Time tracker",
          icon: Clock,
          active: isTimeTrackerRoute,
          onClick: () => navigate("/internal/time-tracker"),
        },
        {
          title: "Project Time Stats",
          icon: CalendarSearch,
          active: isProjectTimeStatsRoute,
          onClick: () => navigate("/internal/time-project"),
        }
      ],
    },
    {
      label: "Analytics",
      items: [
        {
          title: "Platform Analytics",
          icon: Activity,
          active: isPlatformAnalyticsRoute,
          onClick: () => navigate("/internal/platform-analytics"),
        },
        ...statsPages.map((page) => ({
          title: page.title,
          icon: getStatsPageIcon(page.id),
          active: activeStatsPageId === String(page.id),
          onClick: () => navigate(`/internal/stats/${page.id}`),
        })),
        {
          title: "New Stats Page",
          icon: Plus,
          active: false,
          onClick: () => setNewPageModalOpen(true),
          isAddAction: true,
          disableRipple: true,
        },
      ],
    },
    {
      label: "Audit Log",
      items: [
        {
          title: "Internal Activities",
          icon: Logs,
          active: location?.pathname === "/internal/internal-activities",
          onClick: () => navigate("/internal/internal-activities"),
        },
      ],
    },
    ...(isAdmin
      ? [
        {
          label: "Team",
          items: [
            {
              title: "Invitations",
              icon: UserPlus,
              active: isInvitationsRoute,
              onClick: () => navigate("/internal/users/invitations"),
            },
          ],
        },
      ]
      : []),
  ];

  const activeGroupedLabel = navGroups.find((group) => group.items.some((item) => item.active))?.label;

  const [expandedGroups, setExpandedGroups] = useState({
    Talent: true,
    Candidates: true,
    Clients: true,
    Content: true,
    "Time tracker": true,
    Analytics: true,
    "Audit Log": true,
  });

  useEffect(() => {
    if (activeGroupedLabel) {
      setExpandedGroups((prev) => ({ ...prev, [activeGroupedLabel]: true }));
    }
  }, [activeGroupedLabel]);

  const toggleGroup = (label) => {
    if (!open) return;
    setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  let toggleButtonSx = {
    width: "100%",
    py: 1,
  };
  let navItemLayoutSx = {
    px: 0,
    py: 1,
    justifyContent: "center",
  };
  let logoutLayoutSx = {
    py: 1,
    justifyContent: "center",
  };

  if (open) {
    toggleButtonSx = {
      p: 1,
    };
    navItemLayoutSx = {
      px: 1.25,
      py: "6px",
      gap: 1.25,
      justifyContent: "flex-start",
    };
    logoutLayoutSx = {
      px: 1.25,
      py: "6px",
      gap: 1.25,
      justifyContent: "flex-start",
    };
  }

  const createCandidate = (values, { reset, resumeFile, autoEmail, close } = {}) => {
    const handleSuccess = () => {
      enqueueSnackbar("Candidate created", { variant: "success" });
      reset?.();
      close?.();
    };

    const handleError = (err) => {
      const messages = err?.response?.data?.errors;
      if (err?.response?.status === 422 && Array.isArray(messages) && messages.length > 0) {
        messages.forEach((msg) => enqueueSnackbar(msg, { variant: "error" }));
        return;
      }
      enqueueSnackbar("Failed to create candidate", { variant: "error" });
    };

    if (!resumeFile) {
      return sh.post("/internal/candidates", {
        candidate: values,
        authenticity_token,
        auto_email: autoEmail,
      })
        .then(handleSuccess)
        .catch(handleError);
    }

    const formData = new FormData();
    formData.append("authenticity_token", authenticity_token);

    const appendValue = (key, value) => appendFormValue(formData, key, value);

    Object.entries(values || {}).forEach(([k, v]) => appendValue(`candidate[${k}]`, v));
    formData.append("candidate[resume]", resumeFile);

    if (autoEmail && typeof autoEmail === "object") {
      Object.entries(autoEmail).forEach(([k, v]) => appendValue(`auto_email[${k}]`, v));
    }

    return sh.post("/internal/candidates", formData)
      .then(handleSuccess)
      .catch(handleError);
  };

  const logout = () => {
    sh.delete("/internal/sign_out", {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      data: {
        authenticity_token,
      },
    })
      .then(() => {
        window.location.replace("/fr");
      })
      .catch((err) => {
        console.error(err);
      });
  };

  return (
    <>
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            position: "fixed",
            top: 0,
            left: 0,
            height: "100dvh",
            bgcolor: "hsl(150 38% 17%)",
            color: "hsl(140 10% 60%)",
            borderRight: "1px solid hsl(150 25% 24%)",
            transition: "width 300ms ease-in-out",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "56px",
              borderBottom: "1px solid hsl(150 25% 24%)",
              justifyContent: open ? "space-between" : "center",
              px: open ? 1.5 : 1,
              minWidth: 0,
            }}
          >
            {open && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                <Avatar
                  src={currentInternalUser?.avatar_url || undefined}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "8px",
                    bgcolor: "hsl(80 97% 55%)",
                    color: "hsl(150 38% 10%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    fontSize: "0.75rem",
                    fontFamily: "Segoe UI Semiblack, sans-serif, system-ui",
                    lineHeight: 1,
                  }}
                >
                  {`${currentInternalUser?.first_name?.[0] || ""}${currentInternalUser?.last_name?.[0] || "S"}`.toUpperCase()}
                </Avatar>
                <Typography
                  component="span"
                  sx={{
                    color: "hsl(80 50% 85%)",
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    fontSize: "0.875rem",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {[currentInternalUser?.first_name, currentInternalUser?.last_name].filter(Boolean).join(" ") || "Squarehub"}
                </Typography>
              </Box>
            )}

            <Tooltip title={open ? "Collapse sidebar" : "Expand sidebar"}>
              <Box
                component="button"
                onClick={() => onToggle?.()}
                type="button"
                aria-label="Toggle sidebar"
                sx={{
                  all: "unset",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: open ? "center" : "center",
                  borderRadius: "6px",
                  color: "hsl(140 10% 60%)",
                  cursor: "pointer",
                  transition: "background-color 150ms ease, color 150ms ease",
                  ...toggleButtonSx,
                  "&:hover": {
                    bgcolor: "hsl(150 30% 22%)",
                    color: "hsl(80 50% 85%)",
                  },
                }}
              >
                {open ? <PanelLeftClose size={16} /> : <PanelLeft size={16} />}
              </Box>
            </Tooltip>
          </Box>

          {open && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1,
                px: 1.5,
                py: 1,
                borderBottom: "1px solid hsl(150 25% 24%)",
              }}
            >
              {[
                {
                  title: "Create candidate",
                  icon: UserPlus,
                  onClick: () => openCreateCandidate({ onSubmit: createCandidate }),
                },
                {
                  title: "Apply to a job offer",
                  icon: FilePlus,
                  onClick: () => setApplyToJobOpen(true),
                },
                {
                  title: "Create external profile",
                  icon: Contact,
                  onClick: () => setCreateProfileOpen(true),
                },
                {
                  title: "New pipeline",
                  icon: GitBranchPlus,
                  onClick: () => setCreatePipelineOpen(true),
                },
              ].map((action) => {
                const ActionIcon = action.icon;
                return (
                  <Tooltip key={action.title} title={action.title} placement="bottom">
                    <Box
                      component="button"
                      type="button"
                      onClick={action.onClick}
                      aria-label={action.title}
                      sx={{
                        all: "unset",
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        border: "1px solid hsl(150 25% 30%)",
                        color: "hsl(80 50% 85%)",
                        cursor: "pointer",
                        flexShrink: 0,
                        transition: "transform 150ms ease, background-color 150ms ease, color 150ms ease, border-color 150ms ease",
                        "&:hover": {
                          bgcolor: "hsl(150 30% 22%)",
                          color: "hsl(80 97% 55%)",
                          borderColor: "hsl(80 97% 55%)",
                          transform: "scale(1.12)",
                        },
                        "&:active": {
                          transform: "scale(0.95)",
                        },
                      }}
                    >
                      <ActionIcon size={16} />
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          )}

          <Box
            component="nav"
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              py: 1,
              px: 1,
              scrollbarColor: "hsl(80 45% 55%) hsl(150 18% 22%)",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": { width: 6, height: 6 },
              "&::-webkit-scrollbar-track": {
                background: "hsl(150 18% 22%)",
                borderRadius: 4,
              },
              "&::-webkit-scrollbar-thumb": {
                background: "hsl(80 45% 55%)",
                borderRadius: 4,
              },
            }}
          >
            {navGroups.map((group, index) => (
              <Box key={group.label} sx={{ mb: 1 }}>
                {!open && index > 0 && (
                  <Box
                    sx={{
                      height: "1px",
                      backgroundColor: "hsl(150 25% 24%)",
                      mx: 0.5,
                      my: 1,
                    }}
                  />
                )}

                {open && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      px: 1,
                      py: "6px",
                    }}
                  >
                    <Box
                      component="button"
                      onClick={() => toggleGroup(group.label)}
                      type="button"
                      sx={{
                        all: "unset",
                        display: "flex",
                        alignItems: "center",
                        flex: 1,
                        textTransform: "uppercase",
                        letterSpacing: "0.09em",
                        fontSize: "10px",
                        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                        color: "hsl(215 10% 50%)",
                        cursor: "pointer",
                        transition: "color 150ms ease",
                        "&:hover": {
                          color: "hsl(80 50% 85%)",
                        },
                      }}
                    >
                      {expandedGroups[group.label] ? (
                        <ChevronDown size={14} style={{ marginRight: 4 }} />
                      ) : (
                        <ChevronRight size={14} style={{ marginRight: 4 }} />
                      )}
                      {group.label}
                    </Box>
                  </Box>
                )}

                {(open ? expandedGroups[group.label] : true) && (
                  <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    {group.items.map((item) => {
                      const Icon = item.icon;

                      return (
                        <Tooltip key={item.title} title={open ? "" : item.title} placement="right">
                          <ListItemButton
                            selected={item.active}
                            onClick={item.onClick}
                            disableRipple={item.disableRipple}
                            sx={{
                              borderRadius: "6px",
                              transition: "background-color 150ms ease, color 150ms ease",
                              ...navItemLayoutSx,
                              ...(item.isAddAction
                                ? {
                                  border: "1px dashed hsl(150 25% 30%)",
                                  color: "hsl(140 10% 60%)",
                                  mt: "4px",
                                  "&:hover": {
                                    bgcolor: "hsl(150 30% 22%)",
                                    color: "hsl(80 50% 85%)",
                                    borderColor: "hsl(80 50% 85%)",
                                  },
                                }
                                : item.active
                                  ? {
                                    bgcolor: "hsl(150 30% 22%)",
                                    color: "hsl(80 97% 55%)",
                                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                                    "&.Mui-selected": {
                                      bgcolor: "hsl(150 30% 22%)",
                                    },
                                    "&:hover, &.Mui-selected:hover": {
                                      bgcolor: "hsl(150 30% 22%)",
                                    },
                                  }
                                  : {
                                    color: "hsl(140 10% 60%)",
                                    "&:hover": {
                                      bgcolor: "hsl(150 30% 22%)",
                                      color: "hsl(80 50% 85%)",
                                    },
                                  }),
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                color: "inherit",
                                mr: 0,
                                justifyContent: "center",
                              }}
                            >
                              <Icon size={16} />
                            </ListItemIcon>
                            {open && (
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: "0.875rem",
                                  lineHeight: 1.5,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {item.title}
                              </Typography>
                            )}
                          </ListItemButton>
                        </Tooltip>
                      );
                    })}
                  </List>
                )}
              </Box>
            ))}

          </Box>

          <Box
            sx={{
              borderTop: "1px solid hsl(150 25% 24%)",
              p: 1,
            }}
          >
            <Tooltip title={open ? "" : "Log out"} placement="right">
              <ListItemButton
                onClick={logout}
                sx={{
                  borderRadius: "6px",
                  transition: "background-color 150ms ease, color 150ms ease",
                  color: "hsl(140 10% 60%)",
                  ...logoutLayoutSx,
                  "&:hover": {
                    bgcolor: "hsl(150 30% 22%)",
                    color: "hsl(80 50% 85%)",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    color: "inherit",
                    justifyContent: "center",
                  }}
                >
                  <LogOut size={16} />
                </ListItemIcon>
                {open && (
                  <Typography component="span" sx={{ fontSize: "0.875rem", lineHeight: 1.5 }}>
                  Log out
                  </Typography>
                )}
              </ListItemButton>
            </Tooltip>
          </Box>
        </Box>
      </Drawer>

      <NewStatsPageModal
        open={newPageModalOpen}
        onClose={() => setNewPageModalOpen(false)}
        onSubmit={handleCreatePage}
        submitting={newPageSubmitting}
      />

      <ApplyToOfferDialog
        open={applyToJobOpen}
        onClose={() => setApplyToJobOpen(false)}
        onApplied={() => window.dispatchEvent(new CustomEvent(JOB_APPLICATION_CREATED_EVENT))}
      />

      <CreateProfileDrawer
        open={createProfileOpen}
        onClose={() => setCreateProfileOpen(false)}
        onCreated={(profile) => {
          setCreateProfileOpen(false);
          enqueueSnackbar("External profile created", { variant: "success" });
          window.dispatchEvent(new CustomEvent(EXTERNAL_PROFILE_CREATED_EVENT, { detail: profile }));
        }}
      />

      <CreatePipelineDrawer
        open={createPipelineOpen}
        onClose={() => setCreatePipelineOpen(false)}
        onCreated={() => {
          setCreatePipelineOpen(false);
          enqueueSnackbar("Pipeline created", { variant: "success" });
          navigate("/internal/pipelines");
        }}
      />
    </>
  );
};

export default InternalAppBar;


